import React, { useState } from 'react';
import { Icon } from 'src/components/widgets/icon';
import CheckSvg from 'src/shared/material_icons/navigation/check.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import styled from 'styled-components';
import { DropdownContent, DropdownList, DropdownListItem } from '../dropdown';
import { useDropdown } from '../hooks/useDropdown';

type AllowedPosition = 'LEFT' | 'RIGHT';

const Container = styled.div<{ $disabled?: boolean; $hasSelection?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 10px;
    height: 32px;
    box-sizing: border-box;
    border-radius: 6px;
    border: 1px solid ${(props) => (props.$hasSelection ? '#E8650A' : '#e5e7eb')};
    background: ${(props) => (props.$hasSelection ? '#fff7ed' : '#fff')};
    font-size: 12px;
    font-weight: 500;
    color: ${(props) => (props.$hasSelection ? '#E8650A' : '#263238')};
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;
    white-space: nowrap;
    position: relative;
    opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
    pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};

    &:hover {
        border-color: ${(props) => (props.$hasSelection ? '#ea6c0b' : '#d1d5db')};
    }
`;

const Label = styled.div`
    user-select: none;
    display: flex;
    align-items: center;
    gap: 4px;

    > span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const DropdownArrow = styled(Icon)`
    width: 16px;
    height: 16px;
    color: inherit;
    line-height: 1;
    display: flex;
    align-items: center;
`;

const CheckIcon = styled(Icon)<{ $visible: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    min-width: 16px;
    flex: 0 0 16px;
    color: #e8650a;
    visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
`;

const FilterListItem = styled(DropdownListItem)`
    align-items: center;
    padding: 6px 8px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 400;
    gap: 8px;
`;

const SelectOptionsContent = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const ClearButton = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #e8650a;
    color: #fff;
    font-size: 12px !important;
    line-height: 1;
    flex: 0 0 14px;
    transition: background 0.12s ease;

    svg {
        width: 10px;
        height: 10px;
        fill: #fff;
    }

    &:hover {
        background: #c75508;
    }
`;

interface IOption<T> {
    label: string | React.ReactNode;
    caption?: React.ReactNode;
    value: T;
    chipLabel?: string;
}

export type SelectionValue<T> = T | T[] | null;

export interface ISelectOptionsProps<T> {
    options: IOption<string>[];
    placeholder: string;
    value?: SelectionValue<T>;
    onChange?: (value: SelectionValue<T>) => void;
    width?: string;
    maxHeight?: string;
    position?: AllowedPosition;
    multiple?: boolean;
    clearable?: boolean;
    updateLabel?: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
}

export const SelectOptions = <T = string,>({
    value,
    options,
    placeholder,
    width,
    position,
    onChange,
    maxHeight = '300px',
    multiple = true,
    clearable = true,
    disabled = false,
    disabledTooltip = ''
}: ISelectOptionsProps<T>) => {
    const [_innerState, _setInnerState] = useState<SelectionValue<T>>(() => (multiple ? [] : null));
    const [setOpen, setClose, isVisible, Dropdown] = useDropdown();
    const isControlled = value !== undefined;
    const normalizedValue = Array.isArray(value) ? value.map((v) => String(v)) : value != null ? String(value) : multiple ? [] : null;
    const selectedValue = isControlled ? (normalizedValue as SelectionValue<T>) : _innerState;

    const setUncontrolledValue = (nextValue: SelectionValue<T>) => {
        if (!isControlled) _setInnerState(nextValue);
    };

    const handleInputChange = (value: T, remove = false) => {
        if (!multiple) {
            onChange?.(value);
            setUncontrolledValue(value);
            return;
        }

        if (!Array.isArray(selectedValue)) {
            onChange?.(value);
            setUncontrolledValue(value);
            return;
        }

        if (remove) {
            const updated = selectedValue.filter((item) => item !== value);
            onChange?.(updated);
            setUncontrolledValue(updated);
            return;
        }

        const updated = [...selectedValue, value];
        onChange?.(updated);
        setUncontrolledValue(updated);
    };

    const handleClearing = () => {
        const nextValue = multiple ? [] : null;
        setUncontrolledValue(nextValue);
        onChange?.(nextValue);
    };

    const hasSelection = multiple ? Array.isArray(selectedValue) && selectedValue.length > 0 : selectedValue != null && selectedValue !== '';

    const SelectContent = (
        <Container
            $disabled={disabled}
            $hasSelection={hasSelection}
            onClick={(ev): void => {
                if (disabled) return;
                ev.stopPropagation();
                if (isVisible) {
                    setClose();
                    return;
                }
                setOpen(ev.currentTarget, 'bottom', position === 'RIGHT' ? 'right' : 'left');
            }}
        >
            <Label>
                <span>
                    {hasSelection
                        ? (() => {
                              const getChipLabel = (val: unknown) => {
                                  const opt = options.find((o) => o.value === val);
                                  return opt?.chipLabel || (typeof opt?.label === 'string' ? opt.label : null);
                              };
                              if (!multiple) {
                                  const name = getChipLabel(selectedValue);
                                  return name ? `${placeholder}: ${name}` : placeholder;
                              }
                              const selected = ((selectedValue as string[]) || []).map(getChipLabel).filter(Boolean);
                              if (selected.length === 0) return placeholder;
                              if (selected.length === 1) return `${placeholder}: ${selected[0]}`;
                              return `${placeholder}: ${selected[0]} +${selected.length - 1}`;
                          })()
                        : placeholder}
                </span>
                {hasSelection && clearable ? (
                    <ClearButton
                        aria-label='Clear'
                        role='button'
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClearing();
                            setClose();
                        }}
                    >
                        <CloseIcon />
                    </ClearButton>
                ) : (
                    <DropdownArrow aria-hidden='true'>
                        <ExpandMoreIcon />
                    </DropdownArrow>
                )}
            </Label>
            {isVisible && (
                <Dropdown surfacePadding='0' minWidth={width} zIndex={50}>
                    <SelectOptionsContent>
                        <DropdownContent $maxHeight={maxHeight}>
                            <DropdownList>
                                {options.map((option, index) => {
                                    const isActive = multiple
                                        ? Array.isArray(selectedValue) && selectedValue.includes(option.value as T)
                                        : selectedValue === option.value;
                                    return (
                                        <FilterListItem
                                            as='div'
                                            role='option'
                                            aria-selected={isActive}
                                            key={`option-${option.value.replaceAll(' ', '')}-${index}`}
                                            $active={isActive}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (multiple) {
                                                    handleInputChange(option.value as T, isActive);
                                                } else {
                                                    handleInputChange(option.value as T);
                                                    setClose();
                                                }
                                            }}
                                        >
                                            <CheckIcon $visible={isActive} aria-hidden='true'>
                                                <CheckSvg />
                                            </CheckIcon>
                                            {option.label}
                                        </FilterListItem>
                                    );
                                })}
                            </DropdownList>
                        </DropdownContent>
                    </SelectOptionsContent>
                </Dropdown>
            )}
        </Container>
    );

    return disabled && disabledTooltip ? <div title={disabledTooltip}>{SelectContent}</div> : SelectContent;
};
